import React from 'react';
import { FaUndo, FaClock, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaCalendarAlt, FaBox } from 'react-icons/fa';

const ReturnListRenderer = ({ data }) => {
  if (!data || !data.returns || data.returns.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-gray-600">
          <FaUndo className="text-sm" />
          <span className="text-sm">No return requests found</span>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <FaClock className="text-yellow-500" />;
      case 'APPROVED':
        return <FaCheckCircle className="text-green-500" />;
      case 'REJECTED':
        return <FaTimesCircle className="text-red-500" />;
      case 'PROCESSED':
        return <FaBox className="text-blue-500" />;
      default:
        return <FaExclamationTriangle className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PROCESSED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getTotalItems = (returnItem) => {
    return returnItem.orders.reduce((total, order) => {
      return total + order.items.reduce((orderTotal, item) => orderTotal + item.quantity, 0);
    }, 0);
  };

  return (
    <div className="space-y-4">
      {data.message && (
        <div className="text-sm text-gray-600 mb-3">
          {data.message}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.returns.map((returnItem) => (
          <div key={returnItem.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <FaUndo className="text-blue-500 text-sm" />
                <div>
                  <h3 className="font-medium text-gray-900">Return Request</h3>
                  <p className="text-sm text-gray-500">ID: {returnItem.id}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(returnItem.approvalStatus)}
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(returnItem.approvalStatus)}`}>
                  {returnItem.approvalStatus}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div className="flex items-center space-x-2">
                <FaBox className="text-gray-400 text-xs" />
                <div>
                  <span className="text-sm text-gray-500">Items</span>
                  <div className="font-medium text-gray-900">{getTotalItems(returnItem)} items</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <FaCalendarAlt className="text-gray-400 text-xs" />
                <div>
                  <span className="text-sm text-gray-500">Created</span>
                  <div className="font-medium text-gray-900">{formatDate(returnItem.metadata?.createdAt)}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <FaCalendarAlt className="text-gray-400 text-xs" />
                <div>
                  <span className="text-sm text-gray-500">Expires</span>
                  <div className="font-medium text-gray-900">{formatDate(returnItem.expiryDate)}</div>
                </div>
              </div>
            </div>
            
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-500">Total Value</span>
                  <div className="font-semibold text-gray-900">
                    {returnItem.total?.currency} ${returnItem.total?.value?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500">Received</span>
                  <div className="font-medium text-gray-900">
                    {returnItem.received ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
            </div>
            
            {returnItem.reason && (
              <div className="border-t border-gray-100 pt-3 mt-3">
                <span className="text-sm text-gray-500">Reason</span>
                <div className="text-sm text-gray-700 mt-1 break-words">
                  {returnItem.reason.details || returnItem.reason.code}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReturnListRenderer;
